import { useQuery, QueryClient, useQueryClient } from "react-query";
import { DocumentSnapshot } from "firebase/firestore";
import { Card } from "@/types/v2";
import { card } from "@/repositories/cards";

interface UseCardsListQueryParams {
  shopId: string;
  currentPage: number;
  pageSize: number;
  sortBy: string | null;
  sortOrder?: "asc" | "desc";
  enabled?: boolean;
  cardNumberFilter?: string;
}

interface PaginatedCardsResult {
  docs: Card[];
  total: number;
  hasMore: boolean;
  currentPage: number;
  totalPages: number;
}

const cursorCache = new Map<string, DocumentSnapshot>();

export const useCardsListQuery = ({
  shopId,
  currentPage,
  pageSize,
  sortBy = "id",
  sortOrder = "desc",
  enabled = true,
  cardNumberFilter,
}: UseCardsListQueryParams) => {
  const queryClient = useQueryClient();

  const { data: totalCards = 0, isLoading: isTotalLoading } = useQuery({
    queryKey: ["cards-total", shopId, cardNumberFilter],
    queryFn: async (): Promise<number> => {
      const searchOptions = [];

      if (cardNumberFilter) {
        searchOptions.push({
          searchParam: "cardNumber",
          opsString: ">=",
          equality: cardNumberFilter.toUpperCase(),
        });
        searchOptions.push({
          searchParam: "cardNumber",
          opsString: "<=",
          equality: cardNumberFilter.toUpperCase() + "\uf8ff",
        });
      }

      return card.countTotal(
        shopId,
        searchOptions.length > 0 ? searchOptions : undefined
      );
    },
    enabled,
    staleTime: 10 * 1000,
  });

  return useQuery({
    queryKey: [
      "paginated-cards",
      shopId,
      currentPage,
      pageSize,
      sortBy,
      sortOrder,
      cardNumberFilter,
    ],
    queryFn: async (): Promise<PaginatedCardsResult> => {
      const filterKey = `${cardNumberFilter || "all"}`;
      const prevPageKey = `paginated-cards-${shopId}-${currentPage - 1}-${pageSize}-${sortBy}-${sortOrder}-${filterKey}`;
      const currentPageKey = `paginated-cards-${shopId}-${currentPage}-${pageSize}-${sortBy}-${sortOrder}-${filterKey}`;

      let startAfterDoc: DocumentSnapshot | undefined;
      if (currentPage > 1) {
        startAfterDoc = cursorCache.get(prevPageKey);

        if (!startAfterDoc) {
          await buildCursorChain(
            shopId,
            currentPage,
            pageSize,
            sortBy ?? "",
            sortOrder,
            cardNumberFilter
          );
          startAfterDoc = cursorCache.get(prevPageKey);
        }
      }

      const searchOptions = [];

      if (cardNumberFilter) {
        searchOptions.push({
          searchParam: "cardNumber",
          opsString: ">=",
          equality: cardNumberFilter.toUpperCase(),
        });
        searchOptions.push({
          searchParam: "cardNumber",
          opsString: "<=",
          equality: cardNumberFilter.toUpperCase() + "\uf8ff",
        });
      }

      const result = await card.paginate(
        shopId,
        pageSize,
        startAfterDoc,
        sortBy ?? "id",
        sortOrder,
        searchOptions.length > 0 ? searchOptions : undefined
      );

      if (result.lastVisible) {
        cursorCache.set(currentPageKey, result.lastVisible);
      }

      const totalPages = Math.ceil(totalCards / pageSize);

      return {
        docs: result.docs as Card[],
        total: totalCards,
        hasMore: result.hasMore,
        currentPage,
        totalPages,
      };
    },
    enabled: enabled && !isTotalLoading,
    staleTime: 10 * 1000,
    cacheTime: 10 * 1000,
    keepPreviousData: false,
    onSuccess: (data) => {
      if (data.hasMore) {
        queryClient.prefetchQuery({
          queryKey: [
            "paginated-cards",
            shopId,
            currentPage + 1,
            pageSize,
            sortBy,
            sortOrder,
            cardNumberFilter,
          ],
          queryFn: async () => {
            const filterKey = `${cardNumberFilter || "all"}`;
            const nextPageKey = `paginated-cards-${shopId}-${currentPage}-${pageSize}-${sortBy}-${sortOrder}-${filterKey}`;
            const startAfterDoc = cursorCache.get(nextPageKey);

            if (!startAfterDoc) return null;

            const searchOptions = [];

            if (cardNumberFilter) {
              searchOptions.push({
                searchParam: "cardNumber",
                opsString: ">=",
                equality: cardNumberFilter.toUpperCase(),
              });
              searchOptions.push({
                searchParam: "cardNumber",
                opsString: "<=",
                equality: cardNumberFilter.toUpperCase() + "\uf8ff",
              });
            }

            const result = await card.paginate(
              shopId,
              pageSize,
              startAfterDoc,
              sortBy ?? "id",
              sortOrder,
              searchOptions.length > 0 ? searchOptions : undefined
            );

            return {
              docs: result.docs as Card[],
              total: totalCards,
              hasMore: result.hasMore,
              currentPage: currentPage + 1,
              totalPages: data.totalPages,
            };
          },
          staleTime: 1 * 30 * 1000,
        });
      }
    },
  });
};

async function buildCursorChain(
  shopId: string,
  targetPage: number,
  pageSize: number,
  sortBy: string,
  sortOrder: "asc" | "desc",
  cardNumberFilter?: string
) {
  let lastDoc: DocumentSnapshot | undefined;

  for (let page = 1; page < targetPage; page++) {
    const filterKey = `${cardNumberFilter || "all"}`;
    const cacheKey = `paginated-cards-${shopId}-${page}-${pageSize}-${sortBy}-${sortOrder}-${filterKey}`;

    if (!cursorCache.has(cacheKey)) {
      const searchOptions = [];

      if (cardNumberFilter) {
        searchOptions.push({
          searchParam: "cardNumber",
          opsString: ">=",
          equality: cardNumberFilter.toUpperCase(),
        });
        searchOptions.push({
          searchParam: "cardNumber",
          opsString: "<=",
          equality: cardNumberFilter.toUpperCase() + "\uf8ff",
        });
      }

      const result = await card.paginate(
        shopId,
        pageSize,
        lastDoc,
        sortBy,
        sortOrder,
        searchOptions.length > 0 ? searchOptions : undefined
      );

      if (result.lastVisible) {
        cursorCache.set(cacheKey, result.lastVisible);
        lastDoc = result.lastVisible;
      }
    } else {
      lastDoc = cursorCache.get(cacheKey);
    }
  }
}

export const invalidateCardsQueries = (
  queryClient: QueryClient,
  shopId?: string
) => {
  queryClient.invalidateQueries({
    queryKey: ["paginated-cards", shopId],
  });

  queryClient.invalidateQueries({
    queryKey: ["cards-total", shopId],
  });

  cursorCache.clear();
};

export const useInvalidateCards = (shopId?: string) => {
  const queryClient = useQueryClient();

  return () => {
    invalidateCardsQueries(queryClient, shopId);
  };
};
